import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, unref as _unref, mergeProps as _mergeProps, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "symptom-edit-info" }
const _hoisted_2 = { class: "symptom-edit-info-description" }
const _hoisted_3 = { class: "symptom-edit-info-caution" }
const _hoisted_4 = { class: "symptom-edit-contents" }
const _hoisted_5 = { class: "symptom-context-edit-textarea-container" }
const _hoisted_6 = { class: "item-calendar-button-container" }
const _hoisted_7 = { class: "item-calendar-container" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = ["onClick"]
const _hoisted_10 = { class: "item-calendar-wrapper" }
const _hoisted_11 = ["onUpdate:modelValue"]
const _hoisted_12 = {
  key: 0,
  class: "item-calendar-placeholder"
}
const _hoisted_13 = { class: "item-calendar-formatted-date" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = { class: "symptom-edit-button-container" }
const _hoisted_16 = ["disabled"]
const _hoisted_17 = { class: "modal-footer-buttons" }

import dayjs from "dayjs";
import "dayjs/locale/ja";

import {
  computed,
  ref,
  reactive,
  getCurrentInstance,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { processing, processend } from "@/utils/view";
import { NoteData, NoteType } from "./types";
import { _$t } from "@/utils/i18n";
import { createMacSafariDatePickerCloser } from "@/utils/mac-safari-date-picker";

import TextArea from "@/views/base/CharCounterTextArea.vue";
import ClinicNote from "@/models/myRecord/clinicNote/clinicNote";
import ConfirmDialog from "@/views/base/CommonMessage.vue";
import MessageDialog from "@/views/base/CommonMessage.vue";
import ErrorDialog from "@/views/base/CommonMessage.vue";
import Modal from "@/views/base/ModalComponent.vue";

// 日付のローカライズ
const componentId = "healthcaredata02-01-e01";

export default /*@__PURE__*/_defineComponent({
  __name: 'EditMultiNote',
  props: {
    title: {},
    hospitalVisitId: {},
    type: {},
    clinicNotes: {}
  },
  emits: ["close", "save", "transcribe"],
  setup(__props: any, { emit: __emit }) {

dayjs.locale("ja");

// --- props ----------------------------------------
const props = __props;

// --- Constants & Variables ------------------------------
const title = ref(props.title);

// 診察ノート
const clinicNotes = ref(
  [...props.clinicNotes].sort((a: NoteData, b: NoteData) => {
    if (!a.input_date && !b.input_date) return 0;
    if (!a.input_date) return 1;
    if (!b.input_date) return -1;
    return dayjs(a.input_date).valueOf() - dayjs(b.input_date).valueOf();
  })
);
if (clinicNotes.value.length === 0) {
  clinicNotes.value.push({ content: "", input_date: "", sub_number: 0 });
}

// 日付付きのinput要素参照用
const datesInput = ref<HTMLInputElement[]>([]);
// macOS Safari 向け date picker クローズ処理
const safariDatePickerCloser = createMacSafariDatePickerCloser();

// ▼ トースト表示用
const { appContext } = getCurrentInstance()!;
const $toast = appContext.config.globalProperties.$toast;

const emit = __emit;

// --- Dialogs -------------------------------------------

// 確認ダイアログ関連情報
let confirmDialogSet = reactive({
  show: false,
  okButtonHide: true,
  okButton: true,
  okName: _$t("clinic_note_multi_edit_button_ok"),
  cancelButton: true,
  cancelName: _$t("clinic_note_multi_edit_button_cancel"),
  message: "", // 表示メッセージ
  open: (message: string): void => {
    processing();
    confirmDialogSet.message = message;
    confirmDialogSet.show = true;
  },
  execute: async (): Promise<void> => {
    processend();
    confirmDialogSet.show = false;
    await save();
    // トースト表示
    $toast({
      // ▼ 表示メッセージ
      //   PCの場合はメッセージ内の<br>が消えます
      message: `${props.title}${_$t("common_message_saved")}`,
      // ▼ 位置指定する場合は設定（単位: px）
      //   指定なしの場合
      //    【PC】表示位置
      //       X:画面左から56px
      //       Y:画面下から20px

      //    【SP】表示位置
      //       X:画面左から16px
      //       Y:画面下から84px（フッターナビから20px上）
      //
      // offsetX: 0,
      // offsetY: 0,
      // ▼ タイムアウト値を変える場合は設定（単位: ms）
      //   デフォルトは2500ms
      // timer: 30000,
      icon: "success",
    });
    emit("save", props.type, clinicNotes.value);
  },
  close: (): void => {
    processend();
    confirmDialogSet.show = false;
  },
});

// メッセージダイアログ関連情報
let messageDialogSet = reactive({
  show: false,
  okButton: true,
  okButtonHide: true,
  okName: _$t("clinic_note_multi_edit_button_ok"), // OK
  message: "",
  open: (message: string): void => {
    processing();
    messageDialogSet.message = message;
    messageDialogSet.show = true;
  },
  close: (): void => {
    processend();
    messageDialogSet.show = false;
    // 診察ノートへ反映
    emit("save", props.type, clinicNotes.value);
  },
});

// エラーダイアログ関連情報
const sessionErr = ref(false);
let errorDialogSet = reactive({
  show: false,
  okButtonHide: true,
  safe: false,
  message: "",
  open: (message: string): void => {
    processing(); // オーバレイを表示
    errorDialogSet.message = message;
    errorDialogSet.show = true;
  },
  close: (): void => {
    processend(); // オーバレイを非表示
    errorDialogSet.show = false;
    errorDialogSet.message = "";
    if (sessionErr.value) {
      window.location.replace("/cms/login");
    }
  },
});

// --- Functions ---------------------------------------------

// 日付整形
function formatDate(date: string | null) {
  return date ? dayjs(date).format("YYYY年M月D日(ddd)") : "";
}

// 保存
async function save() {
  // 未入力ノートは保存対象から除外する
  const clinicNotesToSave = clinicNotes.value.filter((item: NoteData) =>
    item.content?.trim()
  );

  // 診察ノートモデル作成
  const clinicNoteModel = new ClinicNote();

  // パラメータ設定
  const params = {
    hospitalVisitId: props.hospitalVisitId,
    type: props.type,
    clinicNotes: JSON.stringify([...clinicNotesToSave]),
  };
  clinicNoteModel.set(params);

  // 保存
  const [_model, response, err] = await clinicNoteModel.saveNote();
  // エラー判定
  if (err) {
    const status = err.error.response.status;
    const decodedMessage = decodeURIComponent(
      err.error?.response?.data?.message
    );
    // セッションエラー
    if (status === 408) sessionErr.value = true;
    errorDialogSet.open(decodedMessage);
    return;
  }
  // データ更新
  clinicNotes.value = response.data;
  // 完了メッセージ
  messageDialogSet.open(_$t("clinic_note_message_saved"));
}

// --- Event Handlers -------------------------------------------

// 行削除
function onRemoveItem(index: number) {
  clinicNotes.value.splice(index, 1);
}

// 行追加
function onAddItem() {
  if (clinicNotes.value.length >= 10) return;
  clinicNotes.value.push({ content: "", input_date: "", sub_number: 0 });
}

// 症状・こころノートの内容を入れる
function onTranscribe() {
  if (clinicNotes.value.length >= 10) return;
  emit("transcribe", props.type, clinicNotes.value);
}

// カレンダー表示

function onDateSelected(event: Event) {
  safariDatePickerCloser.onDateSelected(event);
}

// 症状ノートの保存
async function onSave() {
  await save();
  // トースト表示
  $toast({
    // ▼ 表示メッセージ
    //   PCの場合はメッセージ内の<br>が消えます
    message: `${props.title}${_$t("common_message_saved")}`,
    // ▼ 位置指定する場合は設定（単位: px）
    //   指定なしの場合
    //    【PC】表示位置
    //       X:画面左から56px
    //       Y:画面下から20px

    //    【SP】表示位置
    //       X:画面左から16px
    //       Y:画面下から84px（フッターナビから20px上）
    //
    // offsetX: 0,
    // offsetY: 0,
    // ▼ タイムアウト値を変える場合は設定（単位: ms）
    //   デフォルトは2500ms
    // timer: 30000,
    icon: "success",
  });
  emit("save", props.type, clinicNotes.value);
}

// ⊕押下: datepickerを表示
function onAddDate(index: number) {
  const el = datesInput.value[index];
  if (!el) return;
  if (typeof el.showPicker === "function") {
    el.showPicker();
    return;
  }
  // SafariなどshowPicker未対応環境向け
  el.focus();
  el.click();
}

// ⊖押下: 日付のクリア
function onClearDate(index: number) {
  clinicNotes.value[index].input_date = "";
}

// モーダルを閉じる
function onClose() {
  emit("close");
}

// --- Vue lifecycle hooks ----------------------------------
const isInActive = computed(() => clinicNotes.value.length >= 10);

onMounted(() => {
  safariDatePickerCloser.enableSafariDateInputClass();
});

onBeforeUnmount(() => {
  safariDatePickerCloser.disableSafariDateInputClass();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(Modal, {
      id: componentId,
      title: `${title.value}${_ctx.$t('common_title_edit')}`,
      showTab: false,
      modalClass: "modal-symptom-edit",
      onClose: onClose
    }, {
      bodyInfo: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.$t("clinic_note_multi_edit_info_description")), 1),
          _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t("clinic_note_multi_edit_info_caution")), 1)
        ]),
        _createElementVNode("ul", _hoisted_4, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(clinicNotes.value, (note, index) => {
            return (_openBlock(), _createElementBlock("li", {
              key: index,
              class: "symptom-edit-contents-item"
            }, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(TextArea, {
                  modelValue: note.content,
                  "onUpdate:modelValue": ($event: any) => ((note.content) = $event),
                  textareaClass: "item-textarea",
                  charCounterClass: "item-textarea-count",
                  placeholder: _ctx.$t('clinic_note_multi_edit_placeholder_note'),
                  width: "600px",
                  minHeight: "100px"
                }, null, 8, ["modelValue", "onUpdate:modelValue", "placeholder"])
              ]),
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  (!note.input_date)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        onClick: ($event: any) => (onAddDate(index)),
                        src: "/server/necs/images/icon_add_circle.svg"
                      }, null, 8, _hoisted_8))
                    : (_openBlock(), _createElementBlock("img", {
                        key: 1,
                        onClick: ($event: any) => (onClearDate(index)),
                        src: "/server/necs/images/icon_symptom_edit_clear_date.svg"
                      }, null, 8, _hoisted_9)),
                  _createElementVNode("div", _hoisted_10, [
                    _withDirectives(_createElementVNode("input", {
                      ref_for: true,
                      ref_key: "datesInput",
                      ref: datesInput,
                      type: "date",
                      class: "item-calendar",
                      "onUpdate:modelValue": ($event: any) => ((note.input_date) = $event),
                      onChange: onDateSelected,
                      onInput: onDateSelected
                    }, null, 40, _hoisted_11), [
                      [_vModelText, note.input_date]
                    ]),
                    (!note.input_date)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(_ctx.$t("clinic_note_multi_edit_placeholder_date")), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("span", _hoisted_13, _toDisplayString(formatDate(note.input_date)), 1)
                  ])
                ]),
                _createElementVNode("button", {
                  type: "button",
                  class: "item-delete-symptom-note active",
                  onClick: ($event: any) => (onRemoveItem(index))
                }, [
                  _cache[0] || (_cache[0] = _createElementVNode("span", null, null, -1)),
                  _createTextVNode(_toDisplayString(_ctx.$t("clinic_note_multi_edit_button_delete")), 1)
                ], 8, _hoisted_14)
              ])
            ]))
          }), 128))
        ]),
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass({
            'symptom-edit-select-daily-note': true,
            'is-inactive': isInActive.value,
          }),
            onClick: onTranscribe
          }, _toDisplayString(_ctx.$t("clinic_note_multi_edit_button_input_daily_note")), 3),
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass({
            'symptom-edit-add-input-field': true,
            'is-inactive': isInActive.value,
          }),
            onClick: onAddItem,
            disabled: clinicNotes.value.length >= 10
          }, _toDisplayString(_ctx.$t("clinic_note_multi_edit_button_add_input_field")), 11, _hoisted_16)
        ])
      ]),
      footerButton: _withCtx(() => [
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("button", {
            onClick: onClose,
            type: "button",
            class: "modal-cansel"
          }, _toDisplayString(_ctx.$t("clinic_note_multi_edit_button_cancel")), 1),
          _createElementVNode("button", {
            onClick: onSave,
            type: "button",
            class: "modal-ok"
          }, _toDisplayString(_ctx.$t("clinic_note_multi_edit_button_save")), 1)
        ])
      ]),
      _: 1
    }, 8, ["title"]),
    (_unref(confirmDialogSet).show)
      ? (_openBlock(), _createBlock(ConfirmDialog, _mergeProps({ key: 0 }, _unref(confirmDialogSet), {
          onOk: _unref(confirmDialogSet).execute,
          onCancel: _unref(confirmDialogSet).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true),
    (_unref(messageDialogSet).show)
      ? (_openBlock(), _createBlock(MessageDialog, _mergeProps({ key: 1 }, _unref(messageDialogSet), {
          onOk: _unref(messageDialogSet).close,
          onCancel: _unref(messageDialogSet).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true),
    (_unref(errorDialogSet).show)
      ? (_openBlock(), _createBlock(ErrorDialog, _mergeProps({ key: 2 }, _unref(errorDialogSet), {
          onOk: _unref(errorDialogSet).close,
          onCancel: _unref(errorDialogSet).close
        }), null, 16, ["onOk", "onCancel"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})